import React, { useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";

import ModalCard from "../../components/ModalCard";
import Plate from "../../components/Plate";
import { T } from "../../constants";
import { cx, useUi } from "../../styles";
import { ON_SITE_TABS, splitOnSite } from "../onSiteTabs";
import { statusLine, whereText } from "./OnSiteColumn";

const useStyles = makeStyles({
  top: {
    position: "sticky",
    top: 0,
    background: T.elevated,
    zIndex: 1,
    paddingBottom: 10,
  },
  summary: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 12,
  },
  figure: { display: "flex", flexDirection: "column", gap: 2 },
  figureLabel: {
    fontSize: 11,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: T.subtleFg,
    fontWeight: 600,
  },
  figureValue: {
    fontSize: 24,
    fontWeight: 600,
    color: T.fg,
    fontVariantNumeric: "tabular-nums",
    lineHeight: 1.1,
  },
  figureNote: { fontSize: 12, color: T.mutedFg },
  track: {
    height: 8,
    borderRadius: 999,
    background: T.muted,
    overflow: "hidden",
    marginTop: 12,
  },
  fill: { height: "100%", background: T.primary, borderRadius: 999 },
  controls: {
    display: "flex",
    gap: 8,
    alignItems: "center",
    flexWrap: "wrap",
    marginTop: 12,
  },
  filters: { display: "flex", gap: 4, flexWrap: "wrap" },
  filter: {
    border: "none",
    background: "transparent",
    color: T.mutedFg,
    borderRadius: 999,
    padding: "5px 10px",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    fontFamily: "inherit",
    '&[aria-selected="true"]': {
      background: T.accent,
      color: T.accentFg,
      fontWeight: 600,
    },
    "&:focus-visible": {
      outline: `2px solid ${T.primary}`,
      outlineOffset: 1,
    },
  },
  search: { marginLeft: "auto", minWidth: 200 },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 13,
    marginTop: 8,
  },
  th: {
    textAlign: "left",
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: T.subtleFg,
    padding: 8,
    borderBottom: `1px solid ${T.borderSoft}`,
    fontWeight: 600,
  },
  row: {
    cursor: "pointer",
    "& td": {
      padding: "9px 8px",
      borderBottom: `1px solid ${T.borderSoft}`,
      verticalAlign: "middle",
      color: T.fg,
    },
    "&:hover td": { background: T.surface },
    "&:focus-visible": { outline: `2px solid ${T.primary}` },
  },
  muted: { fontSize: 12, color: T.mutedFg },
  kind: {
    fontSize: 11,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: T.primary,
    fontWeight: 600,
  },
  empty: {
    padding: "24px 8px",
    textAlign: "center",
    color: T.mutedFg,
    fontSize: 13,
  },
  small: { padding: "6px 10px", fontSize: 12 },
  note: { fontSize: 12, color: T.mutedFg, marginTop: 10 },
});

export default function ParkedByLotModal({
  lot,
  sessions = [],
  now = Date.now(),
  onClose,
  onOpenCar,
}) {
  const s = useStyles();
  const ui = useUi();
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const lotSessions = sessions;
  const tabs = useMemo(() => splitOnSite(lotSessions, now), [lotSessions, now]);
  const normalisedQuery = query.trim().toLowerCase();
  const rows = (tabs[filter] ?? tabs.all).filter((car) => {
    if (!normalisedQuery) return true;

    return [car.plate, car.guestName, car.bookingId, car.bay]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(normalisedQuery));
  });
  const occupied = lot.occupied;
  const available = lot.capacity === null
    ? null
    : Math.max(0, lot.capacity - occupied);
  const ratio = lot.capacity > 0 ? Math.min(1, occupied / lot.capacity) : 0;
  const openCar = (car) => {
    onClose();
    onOpenCar(car);
  };

  return (
    <ModalCard
      title={`${lot.name} · ${lot.type === "OFFSITE" ? "Off-site" : "On-site"}`}
      subtitle="Active valet sessions currently assigned to this lot."
      onClose={onClose}
      width={860}
      z={1250}
      footerLeft={
        <button
          type="button"
          className={cx(ui.mutedBtn, s.small)}
          onClick={onClose}
        >
          Close
        </button>
      }
    >
      <div className={s.top}>
        <div className={s.summary} aria-label="Lot summary">
          <div className={s.figure}>
            <span className={s.figureLabel}>Capacity</span>
            <span className={s.figureValue}>{lot.capacity ?? "-"}</span>
            <span className={s.figureNote}>configured spaces</span>
          </div>
          <div className={s.figure}>
            <span className={s.figureLabel}>Occupied</span>
            <span className={s.figureValue}>{occupied}</span>
            <span className={s.figureNote}>active valet sessions</span>
          </div>
          <div className={s.figure}>
            <span className={s.figureLabel}>Available now</span>
            <span className={s.figureValue}>{available ?? "-"}</span>
            <span className={s.figureNote}>capacity minus occupied</span>
          </div>
        </div>

        <div
          className={s.track}
          role="img"
          aria-label={`${lot.name} ${occupied} of ${lot.capacity ?? "unknown capacity"} occupied`}
        >
          <div className={s.fill} style={{ width: `${ratio * 100}%` }} />
        </div>

        <div className={s.controls}>
          <div className={s.filters} role="tablist" aria-label="Lot filter">
            {ON_SITE_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                className={s.filter}
                aria-selected={filter === tab.id}
                onClick={() => setFilter(tab.id)}
              >
                {tab.label} ({(tabs[tab.id] ?? []).length})
              </button>
            ))}
          </div>
          <input
            className={cx(ui.field, s.search)}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search plate, guest or booking"
            aria-label="Search this lot"
          />
        </div>
      </div>

      {rows.length === 0 ? (
        <div className={s.empty}>
          {lotSessions.length === 0
            ? "This lot has no active valet sessions."
            : "No active valet session matches this filter."}
        </div>
      ) : (
        <table className={s.table} aria-label={`${lot.name} active valet sessions`}>
          <thead>
            <tr>
              <th className={s.th}>Plate</th>
              <th className={s.th}>Guest</th>
              <th className={s.th}>Location</th>
              <th className={s.th}>Status</th>
              <th className={s.th}>Runner</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((car) => (
              <tr
                key={car.id}
                className={s.row}
                role="button"
                tabIndex={0}
                aria-label={`${car.plate} ${car.guestName || "Unknown vehicle"}`}
                onClick={() => openCar(car)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    openCar(car);
                  }
                }}
              >
                <td>
                  <Plate size={12}>{car.plate}</Plate>
                </td>
                <td>
                  {car.guestName || "Unknown vehicle"}
                  <div className={s.muted}>{car.bookingId || "No booking"}</div>
                </td>
                <td>{whereText(car)}</td>
                <td>
                  <span className={s.kind}>{statusLine(car)}</span>
                </td>
                <td>{car.pickedByName || car.parkedByName || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div className={s.note}>
        {rows.length} active session{rows.length === 1 ? "" : "s"} shown · All
        is selected when the modal opens.
      </div>
    </ModalCard>
  );
}
